import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http, formatApiError, toastApiError, getUserLabels } from "../lib/api";
import { useNavDraft, saveDraftsForReturn } from "../lib/navDraft";
import { fmtCreator } from "../lib/format";
import { fmtTL, fmtDate } from "../lib/format";
import { toast, focusOdak } from "../lib/notify";
import { safePath } from "../lib/nav";
import { Trash2, Lock, CheckCircle2, Calculator, Wallet, Copy, FileSpreadsheet, AlertTriangle, ExternalLink } from "lucide-react";
import SearchableSelect from "../components/SearchableSelect";
import { trUpper } from "../lib/uppercase";
import { useAuth } from "../context/AuthContext";
import { MissingCostProjects } from "../components/MissingCostProjects";
import { CostItemsTable } from "../components/projectcost/CostItemsTable";
import { parseTL, fmtAmountInput } from "../components/AmountInput";
import { formKeyNavWith } from "../lib/formNav";
import { CostCompare } from "../components/projectcost/CostCompare";
import { DeletedCostsList } from "../components/projectcost/DeletedCostsList";
const PROJE_GIDERLERI = "PROJE GİDERLERİ";
const parseAmt = (v) => parseTL(v) || 0;

export default function ProjectCostEntry() {
  const { isAdmin } = useAuth();
  const [userLabels, setUserLabels] = useState({});
  useEffect(() => { getUserLabels().then(setUserLabels); }, []);
  const [claims, setClaims] = useState([]);
  const [subClaims, setSubClaims] = useState([]);   // taşeron hakedişleri — maliyet analizi için müşteri + taşeron hakedişi birlikte gerekir
  const [costs, setCosts] = useState([]);
  const [deletedCosts, setDeletedCosts] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [draftClaims, setDraftClaims] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [period, setPeriod] = useState("");
  const [activeKey, setActiveKey] = useState("");
  const [vals, setVals] = useState({});
  const [extras, setExtras] = useState([]);
  const [copied, setCopied] = useState(null); // { period, byItem: { budget_item_id: { amount, desc } } }
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jumped = useRef(false);
  // "Maliyeti eksik projeler" hücresinden gelindi: ESC / Vazgeç / Maliyet Oluştur → geldiği yere (?don= başka ekran, back aynı sayfadaki hücre + o anki dönem/proje filtresi)
  const [back, setBack] = useState(null); // { key, period, projectId }
  const backKey = back?.key || "";
  const don = safePath(searchParams.get("don"));
  const returnBack = useCallback(() => {
    if (don) { navigate(don); return; }
    if (back) { setPeriod(back.period); setProjectId(back.projectId); focusOdak(back.key); setBack(null); }
  }, [don, back, navigate]);
  useEffect(() => {
    if (!don && !back) return;
    const k = (e) => { if (e.key === "Escape") { e.preventDefault(); setActiveKey(""); returnBack(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [don, back, returnBack]);
  // taslak geri yüklenince URL'deki donem/proje atlaması çalışmasın (formu sıfırlardı)
  useNavDraft("proje-maliyet", () => (activeKey ? { projectId, period, activeKey, vals, extras, note, copied } : null),
    (d) => { jumped.current = true; setProjectId(d.projectId); setPeriod(d.period); setActiveKey(d.activeKey); setVals(d.vals || {}); setExtras(d.extras || []); setNote(d.note || ""); setCopied(d.copied || null); });
  // Yeni gider kalemi: taslağı sakla → Bütçe Kalemleri'nde PROJE GİDERLERİ altına yeni alt kalem formu açık gelsin; Kaydet veya ESC ile aynı adrese ve taslağa dön
  const addBudgetItem = () => {
    const back = window.location.pathname + window.location.search;
    saveDraftsForReturn(back);
    navigate(`/tanimlar/butce-kalemleri?yeni=gider&odak=1&don=${encodeURIComponent(back)}`);
  };

  const fetchBudget = useCallback(() => http.get("/budget-items").then((r) => setBudgetItems(r.data || [])).catch(() => {}), []);
  const load = () => {
    http.get("/claims/history", { params: { direction: "income" } }).then((r) => { setClaims((r.data || []).filter((c) => !c.is_draft)); setDraftClaims((r.data || []).filter((c) => c.is_draft)); });
    http.get("/claims/history", { params: { direction: "expense" } }).then((r) => setSubClaims(r.data || [])).catch(() => {});
    http.get("/project-costs").then((r) => setCosts(r.data));
    http.get("/project-costs/deleted").then((r) => setDeletedCosts(r.data || [])).catch(() => {});
    http.get("/projects").then((r) => setAllProjects((r.data || []).filter((p) => p.status !== "Pasif")));
    fetchBudget();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);   // yalnız ilk açılışta (load her render'da yeniden oluşur)
  useEffect(() => { window.addEventListener("focus", fetchBudget); return () => window.removeEventListener("focus", fetchBudget); }, [fetchBudget]);

  const expenseItems = useMemo(() => {
    const parent = budgetItems.find((b) => !b.parent_id && trUpper(b.name || "") === PROJE_GIDERLERI);
    return parent ? budgetItems.filter((b) => b.parent_id === parent.id && b.status === "Aktif") : [];
  }, [budgetItems]);
  const openForm = useCallback((categoryId) => { setActiveKey(categoryId); setVals({}); setExtras([]); setCopied(null); setNote(""); fetchBudget(); }, [fetchBudget]);

  const prevCost = useMemo(() => {
    if (!projectId || !period || !activeKey) return null;
    const older = costs.filter((k) => k.project_id === projectId && k.period < period).sort((a, b) => b.period.localeCompare(a.period));
    return older.find((k) => k.category_id === activeKey) || older[0] || null;
  }, [costs, projectId, period, activeKey]);

  const copyFromPrev = () => {
    if (!prevCost) return;
    const byItem = {}; const next = {}; let skipped = 0;
    for (const it of prevCost.items || []) {
      const b = expenseItems.find((x) => x.id === it.budget_item_id) || expenseItems.find((x) => trUpper(x.name) === trUpper(it.name || ""));
      if (!b) { skipped++; continue; }
      const amount = fmtAmountInput(it.amount), desc = it.description || "";
      next[b.id] = { amount, desc }; byItem[b.id] = { amount, desc };
    }
    setVals(next); setExtras([]); setCopied({ period: prevCost.period, byItem });
    toast.success(`${prevCost.period} dönemi maliyeti taslak olarak dolduruldu (${Object.keys(byItem).length} kalem${skipped ? `, ${skipped} kalem listede yok — atlandı` : ""})`);
  };

  const exportCost = async (k) => {
    try {
      const r = await http.get(`/project-costs/${k.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `maliyet_${(k.project_name || "proje").replace(/[^\p{L}\p{N}]+/gu, "_")}_${k.period}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toastApiError(e); }
  };

  const periods = useMemo(() => {
    const s = new Set(claims.map((c) => c.period).filter(Boolean));
    if (period) s.add(period);
    return [...s].sort().reverse();
  }, [claims, period]);

  const jumpTo = useCallback((pid, m, catName, catId) => {
    setPeriod(m); setProjectId(pid);
    const all = claims.filter((c) => c.project_id === pid && c.period === m);
    const byCat = (c) => (catId && c.category_id === catId) || (catName && trUpper(c.category_name || "") === trUpper(catName));
    const doneCl = all.find((c) => byCat(c) && costs.some((k) => k.project_id === pid && k.period === m && k.category_id === c.category_id));
    if (doneCl) {   // maliyeti girilmiş kalem (yeşil hücre): mevcut maliyet kaydını göster ve vurgula
      setActiveKey("");
      setTimeout(() => focusOdak(`pcost-claim-${doneCl.invoice_no}`), 150);
      return;
    }
    const cand = all.filter((c) => !costs.some((k) => k.project_id === pid && k.period === m && k.category_id === c.category_id));
    const cl = cand.find(byCat) || cand[0];
    openForm(cl ? cl.category_id : "");
    setTimeout(() => document.querySelector("[data-testid=pcost-project-step]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [claims, costs, openForm]);
  useEffect(() => {
    const d = searchParams.get("donem"), pj = searchParams.get("proje");
    if (d && pj && claims.length > 0 && !jumped.current) { jumped.current = true; jumpTo(pj, d, null, searchParams.get("kategori")); }   // hakedişler yüklenmeden atlama (yarış: costs önce gelirse form açılmazdı)
  }, [claims, costs, searchParams, jumpTo]);
  // Taşeron hakedişi: aynı proje + dönem (+ ana başlık; eski kayıtlarda ana başlık boş olabilir) — onaylı olan öncelikli
  const subClaimFor = useCallback((cl) => {
    const m = subClaims.filter((s) => s.project_id === cl.project_id && s.period === cl.period && (!s.category_id || s.category_id === cl.category_id));
    return m.find((s) => !s.is_draft) || m[0];
  }, [subClaims]);
  const costBack = (cl) => `/veri-girisi?bolum=maliyet&donem=${period}&proje=${projectId}${cl ? `&kategori=${cl.category_id}` : ""}`;
  // Hakediş oluşturmaya git: taslak saklanır; Hakediş Oluştur / Onayla ya da ESC ile aynı dönem + proje + ana başlığa dönülür
  const gotoClaim = (kind, cl) => {
    const back = costBack(cl);
    const [y, m] = period.split("-").map(Number);
    const span = `bas=${period}-01&bit=${period}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
    saveDraftsForReturn(back);
    navigate(`/${kind === "sub" ? "taseron" : "musteri"}-hakedis?proje=${projectId}${cl ? `&ana=${cl.category_id}` : ""}&${span}&don=${encodeURIComponent(back)}`);
  };
  // Taslak taşeron faturasını onaylamaya git (fatura modalı): Kaydet / Kapat / ESC ile buraya dönülür
  const gotoInvoice = (inv, cl) => { const back = costBack(cl); saveDraftsForReturn(back); navigate(`/veri-girisi?bolum=fatura&fatura=${inv.id}&don=${encodeURIComponent(back)}`); };

  const projects = useMemo(() => {
    const m = new Map();
    claims.filter((c) => !period || c.period === period).forEach((c) => { if (c.project_id) m.set(c.project_id, c.project_name || "—"); });
    if (projectId && !m.has(projectId)) { const p = allProjects.find((x) => x.id === projectId); if (p) m.set(p.id, p.name); }
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [claims, period, projectId, allProjects]);

  const noClaimReason = useMemo(() => {
    if (!projectId || !period) return "";
    return draftClaims.some((c) => c.project_id === projectId && c.period === period)
      ? "Hakediş oluşturulmuş ancak faturası henüz onaylanmamış (taslak). Maliyet girişi için önce faturayı onaylayın."
      : "Bu dönem için bu projeye müşteri hakedişi oluşturulmamış. Önce Müşteri Hakedişi ekranından hakediş oluşturup faturasını onaylayın.";
  }, [draftClaims, projectId, period]);

  const periodClaims = useMemo(() => claims.filter((c) => c.project_id === projectId && c.period === period), [claims, projectId, period]);

  const pendingAll = useMemo(() => claims.filter((c) => c.period === period && !costs.some((k) => k.project_id === c.project_id && k.period === c.period && k.category_id === c.category_id)), [claims, costs, period]);

  const costOf = (categoryId) => costs.find((k) => k.project_id === projectId && k.period === period && k.category_id === categoryId);

  const setVal = (id, patch) => setVals((v) => ({ ...v, [id]: { ...(v[id] || {}), ...patch } }));
  const allItems = useMemo(() => [
    ...expenseItems.map((b) => ({ budget_item_id: b.id, name: b.name, amount: parseAmt(vals[b.id]?.amount), description: (vals[b.id]?.desc || "").trim() })),
    ...extras.map((r) => { const b = expenseItems.find((x) => x.id === r.budget_item_id); return { budget_item_id: r.budget_item_id, name: b?.name || "", amount: parseAmt(r.amount), description: (r.desc || "").trim() }; }),
  ], [expenseItems, vals, extras]);
  const total = allItems.reduce((a, r) => a + r.amount, 0);

  const createCost = async (claim) => {
    if (extras.some((r) => parseAmt(r.amount) > 0 && !r.budget_item_id)) return toast.error("Eklenen satırlarda gider kalemi seçin");
    const items = allItems.filter((r) => r.amount > 0 && r.budget_item_id);
    if (!items.length) return toast.error("En az bir gider kalemine tutar girin");
    if (copied) {
      const unchanged = expenseItems.filter((b) => {
        const c = copied.byItem[b.id]; const v = vals[b.id];
        return c && v && parseAmt(v.amount) > 0 && parseAmt(v.amount) === parseAmt(c.amount) && (v.desc || "").trim() === (c.desc || "").trim();
      });
      if (unchanged.length) {
        const list = unchanged.map((b) => `• ${b.name} (${fmtTL(parseAmt(vals[b.id].amount))})`).join("\n");
        if (!window.confirm(`Şu maliyet kalemlerini ${copied.period} döneminden kopyaladınız ancak değiştirmediniz:\n\n${list}\n\nBu haliyle kaydedilecek. Kaydedilsin mi?`)) return;
      }
    }
    setSaving(true);
    try {
      await http.post("/project-costs", { project_id: projectId, period, category_id: claim.category_id, items, note });
      toast.success("Maliyet oluşturuldu — bu hakediş için veri girişi kilitlendi");
      setVals({}); setExtras([]); setCopied(null);
      setNote("");
      setActiveKey("");
      await load();
      returnBack();
    } catch (e) { toastApiError(e); } finally { setSaving(false); }
  };

  const removeCost = async (id) => {
    if (!window.confirm("Maliyet silinsin mi? Silinince bu hakediş için yeniden veri girişi açılır.")) return;
    try { await http.delete(`/project-costs/${id}`); toast.success("Maliyet silindi — hakediş yeniden girişe açıldı"); await load(); }
    catch (e) { toastApiError(e); }
  };

  return (
    <div className="space-y-6" data-testid="page-project-cost-entry" data-esc-trap={back ? "1" : undefined}>
      {back && !activeKey && (
        <button type="button" onClick={returnBack} data-testid="pcost-return-pill" data-return-bar="1" title="Eksik projeler listesindeki hücreye aynı dönem / proje filtresiyle dön (ESC)"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold shadow-lg hover:bg-slate-700">
          ← İptal / Geri dön <kbd className="px-1 rounded bg-white/20 text-[10px] font-mono">ESC</kbd>
        </button>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`rounded-lg border-2 p-4 space-y-2 transition-colors ${period ? "border-emerald-300 bg-emerald-50/40" : "border-amber-400 bg-amber-50/60"}`} data-testid="pcost-period-step">
          <label className="block text-sm font-bold text-slate-900 uppercase tracking-wide">1. Adım — Hakediş Dönemi Seçin</label>
          <select data-testid="pcost-period" value={period} onChange={(e) => { setPeriod(e.target.value); setProjectId(""); setActiveKey(""); }} className="w-full px-3 py-3 text-base font-semibold border border-slate-300 rounded-md bg-white">
            <option value="">Dönem seçin…</option>
            {periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {!period && <div className="text-xs text-amber-800 font-medium" data-testid="pcost-hint">Yalnızca faturası onaylanmış müşteri hakedişi olan dönemler listelenir.</div>}
        </div>
        <div className={`rounded-lg border-2 p-4 space-y-2 transition-colors ${!period ? "border-slate-200 bg-slate-50 opacity-60" : projectId ? "border-emerald-300 bg-emerald-50/40" : "border-amber-400 bg-amber-50/60"}`} data-testid="pcost-project-step">
          <label className="block text-sm font-bold text-slate-900 uppercase tracking-wide">2. Adım — Müşteri (Proje) Seçin</label>
          <div className="[&_input]:py-3 [&_input]:text-base [&_input]:font-semibold">
            <SearchableSelect testid="pcost-project" value={projectId} onChange={(v) => { setProjectId(v); setActiveKey(""); }}
              placeholder={period ? "Proje adı yazarak arayın…" : "Önce dönem seçin"} options={projects} disabled={!period} />
          </div>
          {period && !projectId && <div className="text-xs text-amber-800 font-medium">Seçili dönemde onaylı hakedişi olan projeler listelenir.</div>}
        </div>
      </div>

      {period && (
        <div className="space-y-2" data-testid="pcost-pending-boxes">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">Maliyet Veri Girişi Bekleyenler</span>
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${pendingAll.length ? "bg-amber-100 text-amber-800 animate-pulse" : "bg-emerald-100 text-emerald-800"}`} data-testid="pcost-pending-count">{pendingAll.length} işlem</span>
          </div>
          {pendingAll.length === 0 ? (
            <div className="bg-white border border-emerald-200 rounded-lg px-4 py-3 text-xs text-emerald-700 font-medium" data-testid="pcost-pending-empty">Bu dönem için maliyet bekleyen hakediş yok — tüm maliyetler girilmiş.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pendingAll.map((cl) => {
                const s = subClaimFor(cl); const ready = s && !s.is_draft;
                return (
                <button key={cl.id} type="button" data-testid={`pcost-pending-${cl.invoice_no}`}
                  onClick={() => { setProjectId(cl.project_id); openForm(cl.category_id); }}
                  className={`text-left bg-white border-2 rounded-lg p-3.5 hover:shadow-sm transition-all ${ready ? "border-amber-300 hover:border-amber-500" : "border-red-300 hover:border-red-500"}`}>
                  <div className="text-sm font-bold text-slate-900">{cl.project_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{cl.category_name} • <span className="font-mono">{cl.invoice_no}</span></div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-sm font-mono font-bold text-amber-700">{fmtTL(cl.total_amount)}</span>
                    {ready
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase"><Calculator className="w-3 h-3" /> Maliyet Bekliyor</span>
                      : s
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-[10px] font-bold uppercase" data-testid={`pcost-pending-subdraft-${cl.invoice_no}`}><AlertTriangle className="w-3 h-3" /> Taşeron Hakedişi Taslak</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase" data-testid={`pcost-pending-nosub-${cl.invoice_no}`}><AlertTriangle className="w-3 h-3" /> Taşeron Hakedişi Yok</span>}
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {projectId && period && (
        <div className="space-y-4" data-testid="pcost-claims">
          {periodClaims.length === 0 && (
            <div className="bg-white border-2 border-red-200 rounded-lg py-6 px-5 text-center space-y-2" data-testid="pcost-no-claim">
              <div className="text-sm font-bold text-red-700">Bu dönem için onaylı müşteri hakedişi yok</div>
              <div className="text-xs text-slate-600" data-testid="pcost-no-claim-reason">{noClaimReason}</div>
              <button type="button" onClick={() => gotoClaim("customer")} data-testid="pcost-goto-customer-claim" title="Müşteri Hakedişi ekranı açılır; Hakediş Oluştur / Onayla ya da ESC ile buraya aynı dönem ve projeyle dönülür"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700"><ExternalLink className="w-3.5 h-3.5" /> Müşteri Hakedişi Oluştur</button>
            </div>
          )}
          {periodClaims.map((cl) => {
            const cost = costOf(cl.category_id);
            const open = activeKey === cl.category_id;
            const sub = subClaimFor(cl);
            const subReady = !!sub && !sub.is_draft;
            return (
              <div key={cl.id} className={`bg-white border rounded-lg overflow-hidden ${cost ? "border-emerald-300" : "border-slate-200"}`} data-testid={`pcost-claim-${cl.invoice_no}`} data-odak={`pcost-claim-${cl.invoice_no}`} tabIndex={-1}>
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-sm font-mono font-semibold text-slate-900">{cl.invoice_no}</span>
                  <span className="text-xs text-slate-500">Ana Başlık: {cl.category_name}</span>
                  <span className="text-xs text-slate-500">Hakediş: {fmtTL(cl.total_amount)}</span>
                  <span className={`text-xs ${subReady ? "text-slate-500" : sub ? "text-orange-700 font-semibold" : "text-red-700 font-semibold"}`} data-testid={`pcost-sub-status-${cl.invoice_no}`}>Taşeron: {sub ? <><span className="font-mono">{sub.invoice_no}</span>{sub.is_draft ? " (taslak)" : ""}</> : "hakediş yok"}</span>
                  {cost ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase" data-testid={`pcost-locked-${cl.invoice_no}`}><Lock className="w-3 h-3" /> Maliyet Oluşturuldu</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">Maliyet Bekliyor</span>
                  )}
                  {!cost && !open && (
                    <button onClick={() => openForm(cl.category_id)} data-testid={`pcost-start-${cl.category_id}`}
                      className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
                      <Calculator className="w-3.5 h-3.5" /> Veri Girişi Yap
                    </button>
                  )}
                </div>

                {cost && (
                  <div className="p-4 space-y-2">
                    <table className="w-full tbl">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Gider Kalemi</th>
                          <th className="px-3 py-1.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold w-44">Tutar</th>
                          <th className="px-3 py-1.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold w-72">Açıklama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cost.items || []).map((it, i) => (
                          <tr key={it.id || `${it.budget_item_id || it.name}-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-xs">{it.name}</td>
                            <td className="numeric-cell px-3 py-1.5 text-xs font-semibold w-44">{fmtTL(it.amount)}</td>
                            <td className="px-3 py-1.5 text-xs text-slate-500">{it.description || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200">
                        <tr>
                          <td className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Toplam Maliyet{cost.note ? ` — Not: ${cost.note}` : ""}</td>
                          <td className="numeric-cell px-3 py-2 text-sm font-bold">{fmtTL(cost.total)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                    <div className="text-[11px] text-slate-400">Oluşturan: {fmtCreator(cost.created_by, cost.created_at, userLabels)}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => exportCost(cost)} data-testid={`pcost-export-${cost.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 text-xs font-semibold border border-emerald-200 hover:bg-emerald-100">
                        <FileSpreadsheet className="w-3.5 h-3.5" /> Excel İndir
                      </button>
                      {isAdmin && (
                        <button onClick={() => removeCost(cost.id)} data-testid={`pcost-delete-${cost.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-50 text-red-700 text-xs font-semibold border border-red-200 hover:bg-red-100">
                          <Trash2 className="w-3.5 h-3.5" /> Maliyeti Sil (yeniden giriş açılır)
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {!cost && open && (
                  <div className="p-4 space-y-3" data-testid={`pcost-form-${cl.category_id}`} onKeyDown={formKeyNavWith(() => { if (!saving && subReady) createCost(cl); })}>
                    {!sub && (
                      <div className="flex items-center gap-2 flex-wrap text-xs font-semibold text-red-700 bg-red-50 border border-red-300 rounded-md px-3 py-2" data-testid={`pcost-sub-missing-${cl.category_id}`}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> <span>Bu hakediş için taşeron hakedişi oluşturulmamış — maliyet analizi için müşteri ve taşeron hakedişlerinin ikisi de gerekir.</span>
                        <button type="button" onClick={() => gotoClaim("sub", cl)} data-testid={`pcost-goto-sub-claim-${cl.category_id}`} title="Taşeron Hakedişi ekranı açılır; Hakediş Oluştur ya da ESC ile buraya aynı dönem, proje ve ana başlıkla dönülür"
                          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700"><ExternalLink className="w-3 h-3" /> Taşeron Hakedişi Oluştur</button>
                      </div>
                    )}
                    {sub && !subReady && (
                      <div className="flex items-center gap-2 flex-wrap text-xs font-semibold text-orange-800 bg-orange-50 border border-orange-300 rounded-md px-3 py-2" data-testid={`pcost-sub-draft-${cl.category_id}`}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> <span>Taşeron hakedişi <span className="font-mono">{sub.invoice_no}</span> taslak — maliyet analizi için faturanın onaylı (bütçe kalemine bağlı) olması gerekir.</span>
                        <button type="button" onClick={() => gotoInvoice(sub, cl)} data-testid={`pcost-goto-sub-invoice-${cl.category_id}`} title="Fatura açılır; bütçe kalemi + KDV seçip Kaydet ile onaylayın — Kaydet / Kapat / ESC ile buraya dönülür"
                          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700"><ExternalLink className="w-3 h-3" /> Faturayı Onayla</button>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-xs text-slate-500">Gider kalemleri <span className="font-semibold">Bütçe Kalemleri → PROJE GİDERLERİ</span> alt kalemlerinden otomatik gelir; tutar girilmeyen kalemler kaydedilmez.</div>
                      {prevCost && (
                        <button type="button" onClick={copyFromPrev} data-testid="pcost-copy-prev" title={`${prevCost.period} dönemi${prevCost.category_name ? ` — ${prevCost.category_name}` : ""} maliyetini taslak olarak doldur`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100">
                          <Copy className="w-3.5 h-3.5" /> Önceki Dönemden Kopyala ({prevCost.period})
                        </button>
                      )}
                    </div>
                    {copied && <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="pcost-copied-note">{copied.period} dönemi tutarları taslak olarak dolduruldu — değiştirmediğiniz kalemler kaydetmeden önce onayınıza sunulur.</div>}
                    {expenseItems.length === 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800" data-testid="pcost-no-expense-items">
                        PROJE GİDERLERİ ana kalemi altında aktif alt kalem yok — <button type="button" onClick={addBudgetItem} className="underline font-semibold">Bütçe Kalemleri</button>'nden ekleyin.
                      </div>
                    )}
                    <CostItemsTable expenseItems={expenseItems} vals={vals} setVal={setVal} extras={extras} setExtras={setExtras} />
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={addBudgetItem} data-testid="pcost-add-budget-item" title="Bütçe Kalemleri'nde PROJE GİDERLERİ altına yeni kalem açılır; Kaydet veya ESC ile buraya aynı filtre ve girdiğiniz tutarlarla dönersiniz" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50 whitespace-nowrap"><Wallet className="w-3.5 h-3.5" /> Yeni Gider Kalemi Tanımla</button>
                      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (opsiyonel)" data-testid="pcost-note" className="flex-1 px-3 py-1.5 text-xs border border-slate-300 rounded-md" />
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                      <div className="text-sm font-semibold">Toplam: <span className="font-mono font-bold" data-testid="pcost-total">{fmtTL(total)}</span></div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => { setActiveKey(""); returnBack(); }} data-testid="pcost-cancel" title={don || backKey ? "Geldiğiniz yere dönülür (ESC)" : undefined} className="px-3 py-2 rounded-md text-xs text-slate-600 hover:text-slate-900">Vazgeç</button>
                        <button onClick={() => createCost(cl)} disabled={saving || !subReady} data-testid={`pcost-save-${cl.category_id}`} title={subReady ? "Ctrl+Enter" : sub ? "Önce taşeron hakediş faturasını onaylayın" : "Önce taşeron hakedişini oluşturun"}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
                          <CheckCircle2 className="w-4 h-4" /> {saving ? "Kaydediliyor…" : "Maliyet Oluştur"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MissingCostProjects period={period} selectedId={projectId} refreshKey={costs.length} testid="pcost-nocost-projects"
        onSelect={(p) => { setProjectId(p.id); setActiveKey(""); }} onSelectMonth={(p, m, c, key) => { setBack({ key, period, projectId }); jumpTo(p.id, m, c); }} />

      <CostCompare costs={costs} initialProjectId={projectId} />

      <div className="space-y-2" data-testid="pcost-list">
        <div className="text-sm font-semibold text-slate-900">Oluşturulan Proje Maliyetleri</div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full tbl">
            <thead className="bg-slate-50">
              <tr>
                {["Proje", "Dönem", "Ana Başlık", "Hakediş No", "Kalem", "Toplam Maliyet", "Oluşturan", "Tarih", ""].map((h, i) => (
                  <th key={h} className={`px-3 py-2 text-xs uppercase tracking-wider text-slate-600 font-semibold ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {costs.map((k) => (
                <tr key={k.id} className="border-t border-slate-100" data-testid={`pcost-list-row-${k.id}`}>
                  <td className="px-3 py-2 text-xs">{k.project_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{k.period}</td>
                  <td className="px-3 py-2 text-xs">{k.category_name || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{k.invoice_no || "—"}</td>
                  <td className="px-3 py-2 text-xs">{(k.items || []).length}</td>
                  <td className="numeric-cell px-3 py-2 text-xs font-semibold">{fmtTL(k.total)}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtCreator(k.created_by, k.created_at, userLabels)}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate((k.created_at || "").slice(0, 10))}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => exportCost(k)} className="text-emerald-700 hover:text-emerald-900 mr-2" title="Excel indir" data-testid={`pcost-list-export-${k.id}`}><FileSpreadsheet className="w-3.5 h-3.5" /></button>
                    {isAdmin && <button onClick={() => removeCost(k.id)} className="text-red-700 hover:text-red-900" data-testid={`pcost-list-del-${k.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              ))}
              {costs.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-sm text-slate-500">Henüz maliyet oluşturulmadı</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <DeletedCostsList items={deletedCosts} userLabels={userLabels} isAdmin={isAdmin} onRestored={load} />
    </div>
  );
}
